import { AlertTriangle, ArrowLeftRight, Cloud, Undo2 } from 'lucide-react'

import { Button, ReceiptFacts } from '@krp/ui-kit'

import '../../../shared/uc1b/journals.css'
import { QueryState } from '../../../shared/uc1b/QueryState'
import type { ReceiptDetailActions, ReceiptDetailVm } from '../model/types'
import { ReceiptLines } from './ReceiptLines'

/**
 * Окно чека AQSI: шапка таблицей «подпись — значение», ниже позиции — компактной таблицей, как список номенклатуры. Цена, ставка,
 * предмет и мера — коды из самого чека, не пересчитанные по сегодняшней карточке; название позиции с ключом товара открывает карточку
 * товара AQSI.
 */
export function ReceiptDetailScreen({ vm, actions }: { vm: ReceiptDetailVm; actions: ReceiptDetailActions }) {
  const detail = vm.detail
  return (
    <section className="aqsi-journal" aria-label={detail === null ? 'Чек' : `Чек ${detail.id}`}>
      <QueryState query={vm.query} emptyText="Чек не найден" onRetry={actions.reload} />
      {detail !== null && (
        <>
          {detail.originText !== null && (
            <p className="aqsi-journal__note" role="status">
              <Cloud size={16} aria-hidden="true" />
              <span>{detail.originText}</span>
            </p>
          )}
          {detail.notes.length > 0 && (
            <ul className="aqsi-journal__list" aria-label="Расхождения">
              {detail.notes.map((note) => (
                <li key={note} className="aqsi-journal__note" data-tone="warning">
                  <AlertTriangle size={16} aria-hidden="true" />
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          )}
          <ReceiptFacts label="Реквизиты чека" facts={[
            { label: 'Вид', value: detail.kindText },
            { label: 'Чек', value: detail.id },
            { label: 'Документ', value: detail.docText },
            { label: 'Время', value: detail.atText },
            { label: 'Касса', value: detail.device },
            { label: 'Кассир', value: detail.cashier },
            { label: 'Смена', value: detail.shiftText },
            { label: 'Наличными', value: detail.cashText },
            { label: 'Картой', value: detail.cashlessText },
            { label: 'Итого', value: detail.totalText },
            { label: 'Фискальный признак', value: detail.fiscalAttribute },
            { label: 'Фискальный документ', value: detail.fiscalText },
            { label: 'Слип эквайринга', value: detail.cardSlip },
            { label: 'Кабинет', value: detail.confirmText },
            { label: 'Локальная продажа', value: detail.saleText },
            { label: 'Возвращено', value: detail.returnedText },
          ]} />
          {detail.saleId !== null && actions.openExchange !== undefined && (
            <p>
              <Button variant="secondary" icon={<ArrowLeftRight size={16} aria-hidden="true" />} onClick={() => actions.openExchange?.(detail.saleId ?? '')}>
                Обмен с AQSI
              </Button>
            </p>
          )}
          {detail.sourceId !== null && actions.openReceipt !== undefined && (
            <p>
              <Button variant="secondary" icon={<Undo2 size={16} aria-hidden="true" />} onClick={() => actions.openReceipt?.(detail.sourceId ?? '')}>
                Открыть исходный чек {detail.sourceId}
              </Button>
            </p>
          )}
          {detail.refunds.length > 0 && (
            <section aria-label="Чеки возврата">
              <h3>Чеки возврата ({detail.refunds.length})</h3>
              <ul className="aqsi-journal__list">
                {detail.refunds.map((refund) => (
                  <li key={refund.id}>
                    {actions.openReceipt === undefined ? (
                      refund.label
                    ) : (
                      <Button variant="ghost" onClick={() => actions.openReceipt?.(refund.id)}>
                        {refund.label}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
          <ReceiptLines lines={detail.lines} openGood={actions.openGood} />
        </>
      )}
      {detail?.refund != null && actions.refund !== undefined && (
        <div className="aqsi-journal__actions">
          <Button
            variant="primary"
            icon={<Undo2 size={16} aria-hidden="true" />}
            disabled={!detail.refund.available}
            onClick={actions.refund}
          >
            Вернуть
          </Button>
          {detail.refund.reason !== null && <span className="aqsi-journal__note">{detail.refund.reason}</span>}
        </div>
      )}
      <div className="aqsi-journal__actions">
        <Button onClick={actions.close}>Закрыть</Button>
      </div>
    </section>
  )
}
